import { Injectable, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, first, switchMap, tap } from 'rxjs/operators';
import { LanguageService } from '../language.service';
import { FormsErrorHandlerService } from '../forms-error-handler.service';
import { CacheService } from '../cache/cache.service';
import { UserService } from './user.service';
import { ContentUpdaterService } from '../content-updater/content-updater.service';;
import { EnvironmentService } from '../environment.service';
import { Router } from '@angular/router';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { catchErrorAndReturn } from 'ngx-unificator/rxjs';
import { PlatformGraphQlService } from '../graphql/platform-graphql.service';
import { ApolloError } from '@apollo/client/errors';
import { CookieService, PlatformService, WindowService } from 'ngx-unificator/services';
import { StagDetectorService } from '../stag-detector.service';
import { ApolloClientErrorCodes } from '../graphql/errors';
import { GlobalFieldsConfig } from '../../helpers/global-field-config';
import { resolveChain } from 'ngx-unificator/helpers';
import { EUR_COUNTRIES } from '../currency.service';
import { CFCacheService } from '../cache/cf-cache.service';
import { USER_EMAIL_COOKIE } from './data/cookie-names.data';
import { ToastService } from '../../shared/components/toast/toast.service';
import { Subject } from 'rxjs';
import { AfterLoginCurrencyDataType } from '../../types/login-page.type';
import { UserBonusesService } from './user-bonuses.service';
import { FpWhishperSDK } from 'fpwhisper-id';
import { UserRestrictionsService } from './user-restrictions.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class UserAuthService {

  public lang = inject(LanguageService);
  private _fb = inject(FormBuilder);
  private _formErrors = inject(FormsErrorHandlerService);
  private _cache = inject(CacheService);
  private _cacheCF = inject(CFCacheService);
  private _user = inject(UserService);
  private _contentUpdater = inject(ContentUpdaterService);
  private _toastMessage = inject(ToastService);
  private _env = inject(EnvironmentService);
  private _router = inject(Router);
  private _platformApi = inject(PlatformGraphQlService);
  private _cookie = inject(CookieService);
  private _platform = inject(PlatformService);
  private _stag = inject(StagDetectorService);
  private _window = inject(WindowService);
  private _bonuses = inject(UserBonusesService);
  private _restrictions = inject(UserRestrictionsService);

  private _registerForm: FormGroup = this._fb.group({
    email: [null, [...GlobalFieldsConfig.email.validator]],
    password: [
      null,
      [...GlobalFieldsConfig.password.validator],
    ],
    username: [null, [Validators.required]],
    currency: [null],
    termsAndConditionsVersion: [false, [Validators.requiredTrue]],
    receivePromo: [false],
  });

  private _loginForm: FormGroup = this._fb.group({
    email: [this._cookie.get(USER_EMAIL_COOKIE) ?? null, [...GlobalFieldsConfig.email.validator]],
    password: [
      null,
      [...GlobalFieldsConfig.password.validator],
    ],
  });

  private _loading = signal<boolean>(false);

  public succsessRegistration$ = new Subject<boolean>();

  public get loginForm(): FormGroup {
    return this._loginForm;
  }

  public get registerForm(): FormGroup {
    return this._registerForm;
  }

  public get loading(): boolean {
    return this._loading();
  }

  /**
   * Login form submit handler
   */
  onLoginSubmit(afterRedirectCurrencyData?: AfterLoginCurrencyDataType[]) {
    if (this._loginForm.invalid) {
      this._formErrors.applyFormErrors(this._loginForm, null, true);
      return;
    }
    const data = { ...this._loginForm.value };
    data.email = data.email?.trim();
    this._loading.set(true);
    this._platformApi
      .usersSignIn(data)
      .pipe(
        switchMap(() => this._user.fetchUserData()),
        switchMap(() => this._cacheCF.updateCache$()),
        tap(res => {
          this._loginForm.reset();
          this._cache.clear();
          this._loading.set(false);
          this._contentUpdater.updateAll();
          this._bonuses.loadBonusList().subscribe();
          if (this._platform.isBrowser) {
            this._cookie.set('registered', '1', 999, '/', window.location.hostname as any);
            this._cookie.set('registered-steps', '1', 999, '/', this._window.nativeWindow.location.hostname);
          }
          this._saveEmailToCookie(data?.email);
          resolveChain(
            () => this._regularLoginRedirect(afterRedirectCurrencyData),
          );
        }),
        first(),
        catchErrorAndReturn(
          null,
          (error: ApolloError) => {
            this._toastMessage.error('t.login-error', {translate: true});
            this._loading.set(false);
        }),
      )
      .subscribe();
  }

  async onRegSubmit() {
    if (this._registerForm.invalid) {
      this._formErrors.applyFormErrors(this._registerForm, null, true);
      return;
    }
    this._loading.set(true);
    const sdk = new FpWhishperSDK();
    sdk.storageDeviceKey = 'fpwhisper_id';
    const fingerprint = await sdk.init();
    const data = { ...this._registerForm.value };
    data.email = data.email?.trim();
    data.termsAndConditionsVersion = 'TAC Version';
    data.privacyPolicyVersion = 'PP_Version';
    data.affiliateParams = this._stag.registerAffiliateParams;
    data.channel = this._stag.registerAffiliateParams?.length ? 'affiliate' : 'web';
    data.language = this.lang.current;
    data.fingerprint = fingerprint;
    this._regSubmit(data);
  }

  public autoLoginByToken(token: string) {
    return this._platformApi.loginPlayerByAutoLoginToken(token).pipe(
      catchErrorAndReturn({data: {result: null}}),
      switchMap(() => this._user.fetchUserData()),
      switchMap(() => this._cacheCF.updateCache$()),
      tap(() => {
        if (this._platform.isBrowser) {
          this._cookie.set('registered', '1', 999, '/', window.location.hostname as any);
        }
      })
    );
  }

  private _regSubmit(data: any) {
    this._platformApi
      .usersSignUp(data)
      .pipe(
        switchMap(() => this._user.fetchUserData()),
        switchMap(() => this._cacheCF.updateCache$()),
        tap(() => {
          this._cache.clear();
          this._contentUpdater.updateAll();
          this.succsessRegistration$.next(true);
          this._user.getAutoLoginToken();
          this._bonuses.loadBonusList().subscribe();
          if (this._platform.isBrowser) {
            this._cookie.set('registered', '1', 999, '/', window.location.hostname as any);
          }
          this._saveEmailToCookie(data?.email);
        }),
        catchErrorAndReturn(
          null,
          (error: ApolloError) => {
            switch (error?.graphQLErrors[0]?.extensions?.code || null) {
              case ApolloClientErrorCodes.PLAYER_ALREADY_EXISTS:
                this._loginForm.get('email')?.setValue(data?.email);
                this._loginForm.get('password')?.setValue(data?.password);
                this.onLoginSubmit();
                break;
              default:
                this._toastMessage.error(error.message);
                break;
            }
        }),
        finalize(() => this._loading.set(false)),
      )
      .subscribe();
  }

  /**
   * Set currency by default according payment mode
   * @private
   */
  private _setCurrencyByDefault() {
    if (EUR_COUNTRIES.includes(this._env.dataEnv()?.country?.short)) {
      this._registerForm.get('currency').setValue('EUR');
    } else {
      this._registerForm.get('currency').setValue(this._env.dataEnv()?.currency?.short || 'CAD');
    }
  }

  public setDefaultCurrency() {
    this._setCurrencyByDefault();
  }

  public generateNick() {
    this._registerForm.get('username')?.setValue(this.generateNickname(10));
  }

  /**
   * Generate random username
   * @private
   */
  public generateNickname(length: any) {
    const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
    }
    return result;
  }

  /**
   * The `logoutUser` function logs out the user, reloads the page in a browser environment, and calls
   * a callback function.
   * @param {CallableFunction} callback - The `callback` parameter is a function that will be called
   * after the user is successfully logged out.
   */
  public logoutUser(callback: CallableFunction) {
    this._platformApi.userLogout().subscribe(e => {
      if (e.data.result) {
        if (this._platform.isBrowser) {
          setTimeout(() => {
            this._window?.nativeWindow?.location.reload();
          }, 250);
        }
        return callback();
      }
    });
  }

  private _regularLoginRedirect(afterRedirectCurrencyData: AfterLoginCurrencyDataType[]) {
    if (this._restrictions.isFullAccountLock()) {
      return;
    }

    const currencyData = afterRedirectCurrencyData?.[this._user.currentWallet.currency];

    if(!currencyData) {
      return this._router.navigate(['/']).then(() => {
        // this._toastMessage.success('t.auth-success', {translate: true});
      });
    }

    if (+this._user.currentWallet.amount < +currencyData?.min) {
      this._router.navigate(['/deposit']).then(() => {
        // this._toastMessage.success('t.auth-success', {translate: true});
      });
    } else {
      this._router.navigate(['/']).then(() => {
        // this._toastMessage.success('t.auth-success', {translate: true});
      });
    }
    return true;
  }

  /**
   * Save user email to cookie for future logins
   * @private
   */
  private _saveEmailToCookie(email: string): void {
    if (!email) return;

    this._cookie.set(
      USER_EMAIL_COOKIE,
      email,
      30,
      '/',
    );
  }

}
